import { useRef, useState, type FC } from "react"

import {
    Button, 
    Stack, 
    Typography, 
    useTheme,
    Snackbar,
    Alert,
    List,
    ListItem,
    Box,
    darken,
    IconButton
} from '@mui/material'
import { 
    DriveFolderUpload, 
    Description,
    Clear
} from '@mui/icons-material'

import { strings } from "../../config/strings"
import { formatBytes } from "../../utils/stringsUtils"

interface FilesSelectorProps {
    accept: string
    files: File []
    errorMessage: string,
    onFilesSelected?: (files: File[]) => void
    onFileDelete?: (file: File) => void
}


export const FilesSelector:FC<FilesSelectorProps> = ({
    accept,
    files,
    errorMessage,
    onFilesSelected,
    onFileDelete
}) => {

    //State

    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)

    //Reference

    const inputRef = useRef<HTMLInputElement | null>(null);


    //Hooks
    
    const theme = useTheme()


    //Handlers

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if(!isDragging) setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) =>{
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
        
        const { files } = e.dataTransfer
        if(files)
            filesValidation(Array.from(files))
    }

    const handleBrowseClick = () => {
        inputRef.current?.click()
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()

        const { files }  = e.target

        if(files)
            filesValidation(Array.from(files))
    }

    const handleSnackbarClose = () => {
        setSnackbarOpen(false)
    }

    const filesValidation = (files: File[]) => {
        const acceptedFormats = accept.split(',')

        const csvFiles = files.filter((file) =>
            acceptedFormats.some( s => file.name.toLowerCase().endsWith(s))
        )

        if (csvFiles.length !== files.length) {
            setSnackbarOpen(true)
        }

        onFilesSelected?.(csvFiles)
    }


    //Render

    const renderFiles = () => {

        return (files && files.length > 0)?(
            <Box sx={{
                backgroundColor: darken(theme.palette.background.paper, 0.05),
                borderColor: theme.palette.divider,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 2,
                mt:2
            }}>
                <List disablePadding>
                    {
                        files.map( (file, index, files) => {
                            return (
                                <ListItem 
                                    disableGutters
                                    key={file.name} 
                                    divider={index < files.length - 1} sx={{px:1, justifyContent: 'space-between'}}
                                >
                                    <Box display='flex' sx={{alignItems: 'center'}}>
                                        <Description/>
                                        <Box pl={2}>
                                            <Typography variant="body2">{file.name}</Typography>
                                            <Typography variant="caption">{formatBytes(file.size)}</Typography>
                                        </Box>
                                    </Box>
                                    <IconButton color='error' onClick={() => onFileDelete?.(file)}>
                                        <Clear/>
                                    </IconButton>
                                </ListItem>
                            )
                        })
                    }
                </List>
            </Box>
            )
        :undefined
    }


    return (
        <Box> 
            <Box sx={{
                    borderWidth: 1,
                    borderStyle: 'dashed',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    p:2,
                    backgroundColor: isDragging?theme.palette.secondary.light:theme.palette.background.paper,
                    transition: "background-color 0.2s, border-color 0.2s",
                }}
                onDragOver={handleDragOver} 
                onDragLeave={handleDragLeave} 
                onDrop={handleDrop}
            >
                <Stack spacing={0.25} alignItems='center'>
                    <DriveFolderUpload fontSize="large"/>
                    <Typography variant='body1'>{strings.file_selector_text}</Typography>
                    <Typography variant='body2'>{strings.or}</Typography>
                    <Button size='small' variant="outlined" onClick={handleBrowseClick}>
                        {strings.action_browse}
                    </Button>
                    <input 
                        hidden
                        accept={accept} 
                        type="file"
                        ref={inputRef}
                        onChange={handleFileChange}
                    />
                </Stack>
            </Box>
            {renderFiles()}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <Alert severity="error" onClose={handleSnackbarClose}>
                    {errorMessage}
                </Alert>
            </Snackbar>
         </Box>
    )
}